/**
 * Auto-save timing, shared by every quality document form.
 *
 * Each form owns its own reactivity — a $effect that reads the whole
 * document (which is what subscribes it to every field) and calls
 * `changed()` — and its own dirty anchor (the JSON of the document as last
 * saved or loaded, kept in $state so the "All changes saved" line stays
 * live). What lives HERE is the part that must not be five slightly
 * different copies: the debounce, the "a save is already in flight, look
 * again later" reschedule, and the final should-this-fire checks.
 *
 * The checks run at FIRE time, not at arm time, deliberately: whether the
 * form is busy, deleted, or (for a new form) holds enough content to be
 * worth creating can all change during the two seconds the timer waits.
 */

export interface AutosaveDeps {
  /** JSON of the document as it stands right now. */
  snapshot: () => string;
  /** JSON of the document as last saved/loaded, or null before either. */
  saved: () => string | null;
  /** A save or delete already in flight? Fire reschedules instead. */
  busy: () => boolean;
  /** Permanently off — the record was deleted out from under the timer. */
  blocked: () => boolean;
  /**
   * May a save happen at all right now? A NEW form answers false until it
   * holds real typed content, so backing out of an untouched form never
   * creates a record.
   */
  ready: () => boolean;
  /** The form's own save, in auto mode (no toast, no re-route). */
  save: () => void;
  /** Quiet time after the last change before firing. Default 2000. */
  delayMs?: number;
}

export interface Autosave {
  /** The document changed — (re)start the quiet-time countdown. */
  changed(): void;
  /** Cancel any pending fire; the form is unmounting or was deleted. */
  stop(): void;
}

export function createAutosave(deps: AutosaveDeps): Autosave {
  const delay = deps.delayMs ?? 2000;
  let timer: ReturnType<typeof setTimeout> | null = null;

  function arm(): void {
    if (timer) clearTimeout(timer);
    timer = setTimeout(fire, delay);
  }

  function fire(): void {
    timer = null;
    if (deps.blocked()) return;
    if (deps.busy()) {
      // A manual save (or a delete) is mid-flight; its outcome changes what
      // needs doing, so look again once it has settled.
      arm();
      return;
    }
    if (deps.snapshot() === deps.saved()) return;
    if (!deps.ready()) return;
    deps.save();
  }

  return {
    changed: arm,
    stop(): void {
      if (timer) clearTimeout(timer);
      timer = null;
    }
  };
}
